// Vincular una empresa a un holding del operador (POST contracts/holdings/:id/companies).
//
// Lo usan la ficha del holding ("Vincular una empresa") y el alta de contratos en modo
// holding. contracts escribe `company.holdingId` con compare-and-set y agrega la empresa a
// `holding.companies[]`: la operación es idempotente, así que un 409 `membership_changed`
// (otra escritura cambió la membresía en el medio) se reintenta una vez sin preguntar.

import { linkHoldingCompany } from '../../../../api/operatorHoldings';
import { apiErrorCode, apiErrorStatus } from '../../cobro/utils/collectionReminders';
import { holdingErrorText } from '../../cobro/utils/holdings';

const MEMBERSHIP_CHANGED_RETRIES = 1;

/**
 * @param {string} holdingId
 * @param {string} companyId
 * @returns {Promise<{ ok: true, result: { holdingId: string, companyId: string,
 *   companyUpdated: boolean, listUpdated: boolean } } | { ok: false, error: unknown }>}
 *   Nunca lanza: el error de axios vuelve en `error` para que quien llama arme el texto
 *   (`holdingErrorText`) y decida si sigue.
 */
export async function linkCompanyToHolding(holdingId, companyId) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const { data } = await linkHoldingCompany(holdingId, companyId);
      return { ok: true, result: data };
    } catch (error) {
      const retryable =
        apiErrorCode(error) === 'membership_changed' && attempt < MEMBERSHIP_CHANGED_RETRIES;
      if (!retryable) return { ok: false, error };
    }
  }
}

/** Holdings (de este operador) en los que ya está la empresa, según el 409 de contracts. */
export const conflictingHoldingIds = (error) => {
  const ids = error?.response?.data?.holdingIds;
  return Array.isArray(ids) ? ids.map(String) : [];
};

/**
 * Texto de un error al vincular. Vincular es más restringido que ver los holdings: un 403
 * (`forbidden_role`) dice quién puede hacerlo. Con `holdingNameOf`, el 409
 * `company_in_other_holding` nombra los otros holdings del operador.
 *
 * @param {unknown} error
 * @param {(key: string, opts?: object) => string} t
 * @param {((id: string) => string|null)} [holdingNameOf]
 */
export function linkErrorText(error, t, holdingNameOf) {
  const code = apiErrorCode(error);
  if (code === 'forbidden_role' || (!code && apiErrorStatus(error) === 403)) {
    return t('Solo un administrador o un superadministrador logístico puede vincular empresas a un holding.');
  }
  const text = holdingErrorText(error, t, 'No se pudo vincular la empresa al holding.');
  if (code !== 'company_in_other_holding' || !holdingNameOf) return text;
  const names = conflictingHoldingIds(error).map((id) => holdingNameOf(id) ?? id);
  return names.length ? `${text} (${names.join(', ')})` : text;
}
